import type { TimeRange } from '@mimir/common/util/timeRange';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import type { ExtendedItemDto } from '@mimir/common/dto/extendedItemDto';
import type { DownloadPlan } from '../fileSync/downloadIdentity';
import { getPremiereSubclipTimeRangeInSeconds } from '../util/premiere';
import type { PremiereSubclip } from '../model/ongoingDownload';
import { getStartTimeSeconds } from '../model/playableItem';
import type { Logger } from '@mimir/common/types/logger';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';

export type SubclipOriginItem = Pick<ExtendedItemDto, 'id' | 'metadata' | 'timecode' | 'mediaFrameRate' | 'technicalMetadata'>;

export function getSubclipToImport(
  logger: Logger,
  plan: DownloadPlan,
  isSubclip: boolean,
  timeRange: TimeRange | null,
  virtualClip: VirtualClipModel | null,
  title: string,
  originItem: SubclipOriginItem
): PremiereSubclip | null {
  const deliver = plan.deliver;
  if (deliver.type === 'DeliverSubclip') {
    // A segment range covering the whole media does not need a subclip object
    // (that would cause a double import). Same for a plain non-subclip import.
    if ((virtualClip === null && !isSubclip) || deliver.whole === true) {
      return null;
    }

    const range = deliver.artifactRange;
    // Source-media-absolute start (seconds) for Premiere timecode alignment:
    //   timeRange present → (timeRange.start + inPoint) / 1000
    //                       timeRange is relative to virtual clip start, inPoint shifts to source coords
    //   timeRange absent  → deliver.startTimeCode (inPoint/1000 for virtual clips, bounds.start otherwise)
    const startTimeOffsetInSeconds = timeRange?.start != null
      ? getPremiereSubclipTimeRangeInSeconds(timeRange.start, virtualClip?.inPoint ?? 0)
      : deliver.startTimeCode;
    return {
      title: title.trim() === '' ? getItemSystemDefaultField(originItem, SystemDefaultField.Title) : title,
      startTime: range.start.approximate,
      endTime: range.end.approximate,
      id: originItem.id,
      premiereStartTimeSeconds: getStartTimeSeconds(logger, originItem, startTimeOffsetInSeconds),
    };
  }

  return null;
}
